import { GetCPGTransactionListData } from "@services/models/Transactions/GetCPGTransactionListModel";
import webSocketService from "@services/websocket/WebsocketServices";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import secureLocalStorage from "react-secure-storage";

export const CPG_TRANSACTION_UPDATE_EVENT = "cpg_transaction_update";

export interface CpgListFilters {
  status?: string;
  payinPayout?: string;
  compliancePending: boolean;
  searchApplied: boolean;
}

function isCpgTransaction(value: unknown): value is GetCPGTransactionListData {
  if (!value || typeof value !== "object") return false;
  const trxnId = (value as { trxnId?: unknown }).trxnId;
  return typeof trxnId === "number" || typeof trxnId === "string";
}

/** Accepts one list row, or the `{ data }` envelope from getCPGTransactionList. */
export function parseCpgTransactionUpdatePayload(
  payload: unknown,
): GetCPGTransactionListData[] {
  if (Array.isArray(payload)) {
    return payload.filter(isCpgTransaction);
  }

  if (!payload || typeof payload !== "object") return [];
  if (isCpgTransaction(payload)) return [payload];

  const data = (payload as { data?: unknown }).data;
  if (Array.isArray(data)) return data.filter(isCpgTransaction);
  if (isCpgTransaction(data)) return [data];

  return [];
}

export function cpgTransactionMatchesFilters(
  transaction: GetCPGTransactionListData,
  filters: CpgListFilters,
): boolean {
  if (filters.searchApplied) return false;

  if (
    filters.status &&
    transaction.status?.toLowerCase() !== filters.status.toLowerCase()
  ) {
    return false;
  }

  const ledger = transaction.ledger?.toUpperCase();
  if (filters.payinPayout === "Payin" && ledger !== "CR") return false;
  if (filters.payinPayout === "Payout" && ledger !== "DR") return false;

  if (
    filters.compliancePending &&
    Number(transaction.compliancePending) !== 1
  ) {
    return false;
  }

  return true;
}

export function applyCpgTransactionUpdate(
  current: GetCPGTransactionListData[],
  incoming: GetCPGTransactionListData,
  filters: CpgListFilters,
): GetCPGTransactionListData[] {
  const incomingId = Number(incoming.trxnId);
  const existingIndex = current.findIndex(
    (row) => Number(row.trxnId) === incomingId,
  );
  const matches = cpgTransactionMatchesFilters(incoming, filters);

  if (!matches) {
    if (existingIndex === -1 || filters.searchApplied) return current;
    return current.filter((row) => Number(row.trxnId) !== incomingId);
  }

  if (existingIndex !== -1) {
    const updated = [...current];
    updated[existingIndex] = incoming;
    return updated;
  }

  if (current.length === 0 || incomingId > Number(current[0].trxnId)) {
    return [incoming, ...current];
  }

  return current;
}

interface UseCpgTransactionListSocketArgs {
  setCpgData: Dispatch<SetStateAction<GetCPGTransactionListData[]>>;
  filterStatus?: string;
  filterPayinPayout?: string;
  filterCompliancePending: boolean;
  appliedSearchTerm: string;
}

export function useCpgTransactionListSocket({
  setCpgData,
  filterStatus,
  filterPayinPayout,
  filterCompliancePending,
  appliedSearchTerm,
}: UseCpgTransactionListSocketArgs) {
  const filtersRef = useRef<CpgListFilters>({
    status: filterStatus,
    payinPayout: filterPayinPayout,
    compliancePending: filterCompliancePending,
    searchApplied: Boolean(appliedSearchTerm),
  });

  filtersRef.current = {
    status: filterStatus,
    payinPayout: filterPayinPayout,
    compliancePending: filterCompliancePending,
    searchApplied: Boolean(appliedSearchTerm),
  };

  useEffect(() => {
    const jwt = secureLocalStorage.getItem("jwt");
    webSocketService.connect({ token: jwt }, true);

    const handleCpgTransactionUpdate = (payload: unknown) => {
      const updates = parseCpgTransactionUpdatePayload(payload);
      if (updates.length === 0) return;

      const filters = filtersRef.current;
      setCpgData((prev) =>
        updates.reduce(
          (rows, update) => applyCpgTransactionUpdate(rows, update, filters),
          prev,
        ),
      );
    };

    webSocketService.onMessage(
      CPG_TRANSACTION_UPDATE_EVENT,
      true,
      handleCpgTransactionUpdate,
    );
  }, [setCpgData]);
}
